import { concat, flow, identity, isEmpty, pull, sortBy, uniq } from 'lodash/fp';
import { IPayloadAction, ScannerActions } from './scanner.actions';
import { IScans } from '../interfaces';
import { UUID } from '../../utilities/uuid';


export function scansReducer(state: IScans, action: IPayloadAction) {
  switch (action.type) {

    case ScannerActions.IMAGES_EXTRACTED:
      return onImagesExtracted(state, action);

    case ScannerActions.NOTHING_TO_SCAN:
      return onNothingToScan(state, action);

    case ScannerActions.FORM_STATUS_TOGGLE:
      return { ...state, formStatus: state.formStatus === 'Published' ? 'Draft' : 'Published' };

    case ScannerActions.IMAGE_SENT:
      return onImageSent(state, action);

    case ScannerActions.SCANS_ENTITIES_RECEIVED:
      return onScansEntitiesReceived(state, action);

    case ScannerActions.RESET_SCANS:
      return {
        ...state,
        entities: {},
        synced: [],
        unsynced: [],
        scoringStatus: 'not-sent',
        scans: [],
        completed: false,
        lastTotal: 0,
        totalScans: 0,
        completedScans: 0,
      };

    case ScannerActions.SUBMIT_TO_SCORING:
      return {
        ...state,
        scoringStatus: 'sending',
      };

    case ScannerActions.SUBMIT_TO_SCORING_ERROR:
      return {
        ...state,
        scoringStatus: 'error',
      };

    case ScannerActions.SUBMIT_TO_SCORING_COMPLETED:
      return {
        ...state,
        scoringStatus: 'processing',
      };

    case ScannerActions.GET_SCANS_SUCCESS:
      return {...state, scans: action.payload};

    case ScannerActions.SCORING_COMPLETED:
      return {
        ...state,
        scoringStatus: 'done',
      };

    default:
      return state;
  }
}

function getTotalScans(entities) {
  return Object.keys(entities).length;
}

function getCompletedScans(entities) {
  return Object
    .keys(entities)
    .map((key) => entities[key])
    .filter((scan) => !isEmpty(scan.status))
    .length;
}

function onScansEntitiesReceived(state, action) {
  const entities = {};
  action.payload.forEach((entity) => {
    entities[entity.id] = {...state.entities[entity.id], ...entity};
  });
  const completedScans = getCompletedScans(entities);
  return {
    ...state,
    completedScans,
    completed: state.totalScans === completedScans && completedScans !== state.lastTotal,
    entities: {
      ...state.entities,
      ...entities,
    },
  };
}

function onImagesExtracted(state, action) {
  const imageBlobs = action.payload.blobs;
  const newEntity = {};
  const newIds = [];

  if (imageBlobs.length % 2 !== 0) {
    throw new Error(
      'It looks like you scanned an odd number of pages in a scanner without DUPLEX scanning enabled.' +
      'Please use a scanner that can read both sides of every page of the test booklet.',
    );
  }

  imageBlobs.forEach((blob) => {
    const id = UUID.v4();

    newEntity[id] = {
      id,
      sessionId: action.payload.sessionId,
      blob,
      formStatus: state.formStatus
    };

    newIds.push(id);
  });

  // used for cross referencing the front / back of duplex scanned pages
  newIds.forEach((id, index) => {
    const direction = (index % 2 === 0) ? 1 : -1;
    newEntity[id].backsideId = newEntity[newIds[index + direction]].id;
  });

  const entities = {
    ...state.entities,
    ...newEntity,
  };

  const lastTotal = state.totalScans;

  return {
    ...state,
    totalScans: getTotalScans(entities),
    lastTotal,
    entities,
    unsynced: concat(state.unsynced, newIds),
  };
}

// eslint-disable-next-line @typescript-eslint/no-unused-vars
function onNothingToScan(state, action) {
  return state;
}

function onImageSent(state, action) {
  const id = action.payload.id;

  const synced = flow(concat, uniq, sortBy(identity))(state.synced, id);
  const unsynced = pull(id, state.unsynced);

  return {
    ...state,
    synced,
    unsynced,
  };

}
